import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Bus as BusIcon, LogOut, User, Shield, MessageSquare, Settings, Activity } from 'lucide-react';
import { authAPI } from '../services/api';
import { UserProfile } from './UserProfile';

interface HeaderProps {
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ activeTab, onTabChange }) => {
  const { user, logout, isAdmin } = useAuth();
  const [showUserProfile, setShowUserProfile] = useState(false);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      logout();
      // Also clear any unauthorized user info
      localStorage.removeItem('unauthorized_user_info');
      toast.success('Logged out successfully');
    } catch (error) {
      // Even if the API call fails, we should still log out locally
      logout();
      // Also clear any unauthorized user info
      localStorage.removeItem('unauthorized_user_info');
      toast.success('Logged out successfully');
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center space-x-3 min-w-0 flex-shrink-0">
            <div className="bg-blue-600 p-2 rounded-lg">
              <BusIcon className="h-6 w-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 truncate">TNSTC Bus Monitor</h1>
              <p className="text-xs text-gray-500 hidden sm:block">Real-time bus availability tracking</p>
            </div>
          </div>

          {/* Navigation (if admin) - Made more compact and responsive */}
          {isAdmin && onTabChange && (
            <div className="hidden lg:flex space-x-1 mx-4">
              <button
                onClick={() => onTabChange('monitoring')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'monitoring'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <BusIcon className="h-4 w-4 inline mr-1" />
                <span className="hidden xl:inline">Bus Monitor</span>
                <span className="xl:hidden">Monitor</span>
              </button>
              <button
                onClick={() => onTabChange('admin')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'admin'
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Shield className="h-4 w-4 inline mr-1" />
                <span className="hidden xl:inline">Admin Panel</span>
                <span className="xl:hidden">Admin</span>
              </button>
              <button
                onClick={() => onTabChange('requests')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'requests'
                    ? 'bg-orange-100 text-orange-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <MessageSquare className="h-4 w-4 inline mr-1" />
                <span className="hidden xl:inline">Access Requests</span>
                <span className="xl:hidden">Requests</span>
              </button>
              <button
                onClick={() => onTabChange('monitoring-status')}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === 'monitoring-status'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Activity className="h-4 w-4 inline mr-1" />
                <span className="hidden xl:inline">Monitoring Status</span>
                <span className="xl:hidden">Status</span>
              </button>
            </div>
          )}

          {/* Mobile Navigation Dropdown (for admin) */}
          {isAdmin && onTabChange && (
            <div className="lg:hidden mx-2">
              <select
                value={activeTab}
                onChange={(e) => onTabChange(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
              >
                <option value="monitoring">🚌 Monitor</option>
                <option value="admin">🛡️ Admin</option>
                <option value="requests">💬 Requests</option>
                <option value="monitoring-status">📊 Status</option>
              </select>
            </div>
          )}

          {/* User Info and Logout - Made more compact */}
          <div className="flex items-center space-x-2 min-w-0 flex-shrink-0">
            {user && (
              <>
                <div className="flex items-center space-x-2">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                  )}
                  <div className="hidden md:block min-w-0">
                    <div className="flex items-center space-x-1">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-32">{user.name}</p>
                      {isAdmin && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          <Shield className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate max-w-32">{user.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    title="User Profile"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="hidden lg:inline ml-1">Profile</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden lg:inline ml-1">Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* User Profile Modal */}
      {showUserProfile && (
        <UserProfile onClose={() => setShowUserProfile(false)} />
      )}
    </header>
  );
}; 